/**
 * Tier 3 dynamic-block OBJECTS parsers — DYN-3 Batch D.
 *
 * Decodes the ACDBBLOCK* records that AutoCAD emits for Tier 3 entities:
 *   - ALIGNMENT parameter (ACDBBLOCKALIGNMENTPARAMETER) — auto-rotation
 *     anchor (no action required).
 *   - ALIGNED distance constraint (ACDBBLOCKCONSTRAINEDLINEARLENGTH,
 *     also occasionally emitted as *_A by some AutoCAD builds) — Euclidean
 *     distance between two points along an arbitrary axis.
 *   - RADIAL constraint (ACDBBLOCKCONSTRAINEDRADIAL) — locks circle radius.
 *   - DIAMETER constraint (ACDBBLOCKCONSTRAINEDDIAMETER) — locks diameter.
 *   - POLAR STRETCH action (ACDBBLOCKPOLARSTRETCHACTION) — combined
 *     rotate + translate + stretch on polar parameters.
 *   - 2D GEOMETRIC CONSTRAINT (ACDBBLOCK2DGEOMETRICCONSTRAINT) — generic
 *     wrapper whose sub-type marker (AcDb2dParallelConstraint, etc.)
 *     identifies the concrete geometric constraint kind.
 *
 * Paired with `dxfBridge.dynamic.writer.tier3.ts` (symmetric emitter).
 *
 * Group-code conventions (reverse-engineered; best effort):
 *   Alignment:
 *     10/20 = position, 50 = defaultAngle, 51 = currentAngle,
 *     70 = alignmentType (0=perpendicular, 1=parallel)
 *   Aligned / Radial / Diameter constraint parameters:
 *     10/20 = pointA / center, 11/21 = pointB / radiusPoint / point1,
 *     12/22 = point2 (diameter only),
 *     140 = default value (distance / radius / diameter),
 *     141 = current value
 *   Polar Stretch action:
 *     330 = owner (parameter), 340[] = parameter handle + selection handles,
 *     40 = distanceMultiplier, 41 = angleOffset,
 *     10/20 / 11/21 = crossingFrame corners
 *   2D Geometric Constraint:
 *     100 = subclass marker "AcDb2dXxxConstraint" (discriminator),
 *     340[] = participating entity handles,
 *     341  = reference line handle (symmetric only)
 */
import type { DxfDynamicBlockObject } from "@dxf-ai-editor/dxf-io";

import type {
  AlignedConstraintParameter,
  AlignmentParameter,
  DiameterConstraintParameter,
  DynamicParameter,
  GeometricConstraint,
  GeometricConstraintKind,
  PolarStretchAction,
  RadialConstraintParameter,
  ValueSet,
} from "./dynamicBlock";
import {
  canonicalDynamicBlockClass,
  collectGroups,
  firstGroup,
  firstInt,
  firstNumber,
  parsePointAt,
} from "./dxfBridge.dynamic.shared";

// ═══════════════════════════════════════════════════════════════════════════
// Value-set decoder — shared across Tier 3 constraint parameters
// ═══════════════════════════════════════════════════════════════════════════

type AxisValueSetCodes = {
  modeCode: number;
  incrementCode: number;
  minCode: number;
  maxCode: number;
  listCode: number;
};

function parseAxisValueSet(
  obj: DxfDynamicBlockObject,
  codes: AxisValueSetCodes,
): ValueSet {
  const groups = obj.rawGroups;
  const modeRaw = firstInt(groups, codes.modeCode) ?? 0;
  const mode: ValueSet["mode"] =
    modeRaw === 1 ? "increment" : modeRaw === 2 ? "list" : "none";
  const listRaw = collectGroups(groups, codes.listCode).map(Number.parseFloat);
  const listValues = listRaw.filter(Number.isFinite);
  const valueSet: ValueSet = {
    mode,
    increment: firstNumber(groups, codes.incrementCode) ?? 0,
    listValues,
  };
  const min = firstNumber(groups, codes.minCode);
  const max = firstNumber(groups, codes.maxCode);
  if (min !== undefined) valueSet.min = min;
  if (max !== undefined) valueSet.max = max;
  return valueSet;
}

// ═══════════════════════════════════════════════════════════════════════════
// Tier 3 parameters
// ═══════════════════════════════════════════════════════════════════════════

function parseAlignmentParameter(obj: DxfDynamicBlockObject): AlignmentParameter {
  const groups = obj.rawGroups;
  const position = parsePointAt(groups, 10, 20);
  const defaultAngle = firstNumber(groups, 50) ?? 0;
  const currentAngle = firstNumber(groups, 51) ?? defaultAngle;
  const typeRaw = firstInt(groups, 70) ?? 0;
  const alignmentType: AlignmentParameter["alignmentType"] =
    typeRaw === 1 ? "parallel" : "perpendicular";
  return {
    kind: "alignment",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? `alignment_${obj.handle}`,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    position,
    defaultAngle,
    currentAngle,
    alignmentType,
  };
}

function parseAlignedConstraintParameter(
  obj: DxfDynamicBlockObject,
): AlignedConstraintParameter {
  const groups = obj.rawGroups;
  const pointA = parsePointAt(groups, 10, 20);
  const pointB = parsePointAt(groups, 11, 21);
  const defaultDistance = firstNumber(groups, 140) ?? 0;
  const currentDistance = firstNumber(groups, 141) ?? defaultDistance;
  const distanceValueSet = parseAxisValueSet(obj, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });
  return {
    kind: "constraint-aligned",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? `constraint_aligned_${obj.handle}`,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    pointA,
    pointB,
    defaultDistance,
    currentDistance,
    distanceValueSet,
    associatedEntities: [],
  };
}

function parseRadialConstraintParameter(
  obj: DxfDynamicBlockObject,
): RadialConstraintParameter {
  const groups = obj.rawGroups;
  const center = parsePointAt(groups, 10, 20);
  const radiusPoint = parsePointAt(groups, 11, 21);
  const defaultRadius = firstNumber(groups, 140) ?? 0;
  const currentRadius = firstNumber(groups, 141) ?? defaultRadius;
  const radiusValueSet = parseAxisValueSet(obj, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });
  return {
    kind: "constraint-radial",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? `constraint_radial_${obj.handle}`,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    center,
    radiusPoint,
    defaultRadius,
    currentRadius,
    radiusValueSet,
    associatedEntities: [],
  };
}

function parseDiameterConstraintParameter(
  obj: DxfDynamicBlockObject,
): DiameterConstraintParameter {
  const groups = obj.rawGroups;
  const center = parsePointAt(groups, 10, 20);
  const point1 = parsePointAt(groups, 11, 21);
  const point2 = parsePointAt(groups, 12, 22);
  const defaultDiameter = firstNumber(groups, 140) ?? 0;
  const currentDiameter = firstNumber(groups, 141) ?? defaultDiameter;
  const diameterValueSet = parseAxisValueSet(obj, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });
  return {
    kind: "constraint-diameter",
    id: obj.handle,
    name:
      firstGroup(groups, 1)?.trim() ?? `constraint_diameter_${obj.handle}`,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    center,
    point1,
    point2,
    defaultDiameter,
    currentDiameter,
    diameterValueSet,
    associatedEntities: [],
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Tier 3 actions — Polar Stretch
// ═══════════════════════════════════════════════════════════════════════════

function parsePolarStretchAction(
  obj: DxfDynamicBlockObject,
  handleToParamId: Map<string, string>,
): PolarStretchAction {
  const groups = obj.rawGroups;
  const parameterHandle =
    firstGroup(groups, 330)?.trim() ?? firstGroup(groups, 340)?.trim() ?? "";
  const parameterId = handleToParamId.get(parameterHandle) ?? parameterHandle;
  const selectionEntityIds = collectGroups(groups, 340)
    .slice(1) // first 340 is the parameter handle
    .map((v) => v.trim())
    .filter((h) => h.length > 0);
  const corner1 = parsePointAt(groups, 10, 20);
  const corner2 = parsePointAt(groups, 11, 21);
  return {
    kind: "polar-stretch",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? "",
    parameterId,
    selectionEntityIds,
    distanceMultiplier: firstNumber(groups, 40) ?? 1,
    angleOffset: firstNumber(groups, 41) ?? 0,
    crossingFrame: { corner1, corner2 },
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Geometric constraint (ACDBBLOCK2DGEOMETRICCONSTRAINT)
// ═══════════════════════════════════════════════════════════════════════════

// Map from AutoCAD's subclass marker string to our GeometricConstraintKind.
// Markers are matched case-insensitively; unknown markers yield undefined
// (caller falls back to preserving the object as raw).
const SUBCLASS_TO_KIND: Record<string, GeometricConstraintKind> = {
  acdb2dcoincidentconstraint: "coincident",
  acdb2dcollinearconstraint: "collinear",
  acdb2dconcentricconstraint: "concentric",
  acdb2dfixedconstraint: "fix",
  acdb2dparallelconstraint: "parallel",
  acdb2dperpendicularconstraint: "perpendicular",
  acdb2dtangentconstraint: "tangent",
  acdb2dequalconstraint: "equal",
  acdb2dsymmetricconstraint: "symmetric",
  acdb2dsmoothconstraint: "smooth",
  acdb2dhorizontalconstraint: "horizontal",
  acdb2dverticalconstraint: "vertical",
};

/**
 * Returns the GeometricConstraintKind for a parsed dynamic-block object,
 * or undefined when the subclass marker doesn't match a known kind (the
 * caller should preserve the raw object in that case).
 */
export function geometricConstraintKindFromObject(
  obj: DxfDynamicBlockObject,
): GeometricConstraintKind | undefined {
  const markers = collectGroups(obj.rawGroups, 100).map((s) =>
    s.trim().toLowerCase(),
  );
  for (const marker of markers) {
    const kind = SUBCLASS_TO_KIND[marker];
    if (kind) return kind;
  }
  return undefined;
}

function parseGeometricConstraint(
  obj: DxfDynamicBlockObject,
  kind: GeometricConstraintKind,
): GeometricConstraint {
  const groups = obj.rawGroups;
  // Collect all 340 handles — these are the participating entity handles.
  const handles340 = collectGroups(groups, 340).map((s) => s.trim());
  // Some AutoCAD variants emit the first 340 as the owning BLOCK_RECORD;
  // we treat that as belonging elsewhere (it is already set in
  // obj.ownerHandle). To be robust we include every non-empty 340.
  const entityIds = handles340.filter((h) => h.length > 0);
  // 341 is the reference line for symmetric constraints.
  const referenceLineId =
    kind === "symmetric" ? firstGroup(groups, 341)?.trim() : undefined;
  const result: GeometricConstraint = {
    id: obj.handle,
    kind,
    entityIds,
  };
  if (referenceLineId && referenceLineId.length > 0) {
    result.referenceLineId = referenceLineId;
  }
  return result;
}

// ═══════════════════════════════════════════════════════════════════════════
// Dispatch tables — exported for reuse in the top-level parser
// ═══════════════════════════════════════════════════════════════════════════

export const TIER3_PARAMETER_CLASSES = new Set([
  "ACDBBLOCKALIGNMENTPARAMETER",
  "ACDBBLOCKCONSTRAINEDLINEARLENGTH",
  "ACDBBLOCKCONSTRAINEDLINEARLENGTH_A",
  "ACDBBLOCKCONSTRAINEDRADIAL",
  "ACDBBLOCKCONSTRAINEDDIAMETER",
]);

export const TIER3_ACTION_CLASSES = new Set([
  "ACDBBLOCKPOLARSTRETCHACTION",
]);

export const TIER3_GEOMETRIC_CONSTRAINT_CLASSES = new Set([
  "ACDBBLOCK2DGEOMETRICCONSTRAINT",
]);

/**
 * Tier 3 parameter dispatcher. Returns `undefined` when the object is not
 * a Tier 3 parameter (so the caller can fall through to Tier 1 / 2 /
 * unknown).
 */
export function tryParseTier3Parameter(
  obj: DxfDynamicBlockObject,
): DynamicParameter | undefined {
  switch (canonicalDynamicBlockClass(obj.type)) {
    case "ACDBBLOCKALIGNMENTPARAMETER":
      return parseAlignmentParameter(obj);
    case "ACDBBLOCKCONSTRAINEDLINEARLENGTH":
    case "ACDBBLOCKCONSTRAINEDLINEARLENGTH_A":
      return parseAlignedConstraintParameter(obj);
    case "ACDBBLOCKCONSTRAINEDRADIAL":
      return parseRadialConstraintParameter(obj);
    case "ACDBBLOCKCONSTRAINEDDIAMETER":
      return parseDiameterConstraintParameter(obj);
    default:
      return undefined;
  }
}

/**
 * Tier 3 action dispatcher. Returns `undefined` when the object is not a
 * Tier 3 action.
 */
export function tryParseTier3Action(
  obj: DxfDynamicBlockObject,
  handleToParamId: Map<string, string>,
): PolarStretchAction | undefined {
  switch (canonicalDynamicBlockClass(obj.type)) {
    case "ACDBBLOCKPOLARSTRETCHACTION":
      return parsePolarStretchAction(obj, handleToParamId);
    default:
      return undefined;
  }
}

/**
 * Tier 3 geometric constraint dispatcher. Returns `undefined` if the
 * object isn't an ACDBBLOCK2DGEOMETRICCONSTRAINT or the subclass marker
 * isn't one of the 12 known kinds — the caller should preserve the raw
 * object under `meta.futureExtensions.rawObjects` in that case.
 */
export function tryParseTier3GeometricConstraint(
  obj: DxfDynamicBlockObject,
): GeometricConstraint | undefined {
  if (!TIER3_GEOMETRIC_CONSTRAINT_CLASSES.has(canonicalDynamicBlockClass(obj.type)))
    return undefined;
  const kind = geometricConstraintKindFromObject(obj);
  if (!kind) return undefined;
  return parseGeometricConstraint(obj, kind);
}
